import {
  getLanguages,
  getProfileUtils,
  getTypeOfUser,
  isPharmacyAdminLoginSelector,
  istermsAgreed,
  language,
  Language,
  pharmacistRegions,
  proInfo,
  ServiceTypes,
  TypeOfUser,
  UmbrellaUserTypes,
  userCoordinates,
  userGeneral,
  userPharmacyInfo,
} from '@pharmaplan/common';
import { FormikProps, FormikValues } from 'formik';
import { useAppSelector } from '../useAppSelector';
import {
  pharmacistSignupFields,
  pharmacySignupFields,
  professionalSignupFields,
} from '../../components/SignupFlow/signupForms';
import { useAppDispatch } from '../useAppDispatch';
import useSelfService from '../useSelfService';
import useProfileFormDailog from './useProfileFormDailog';
import {
  pharmacistFields,
  pharmacyField,
  professionalFields,
} from '../../components/Profile/ProfileForm/Forms';
import useUserDelete from '../useUserDelete';
import { resetDialog } from '../../reducers/dialogReducer';

const { self, full } = ServiceTypes;
const { pharmacist, pharmacy, technician, student } = TypeOfUser;
const { professional } = UmbrellaUserTypes;

const useProfileForm = (
  formik: FormikProps<any>,
  setSelected?: React.Dispatch<React.SetStateAction<string>>,
  handleEdit?: () => void,
) => {
  const dispatch = useAppDispatch();

  const {
    showTermsAndConditions,
    showPictureUploadModal,
    showMyGeoAvailability,
  } = useProfileFormDailog();

  const { isSelfService } = useSelfService();
  const { handleDelete } = useUserDelete();

  const allLanguages = useAppSelector(getLanguages);
  const proInfoData = useAppSelector(proInfo);
  const general = useAppSelector(userGeneral);
  const proUtils = useAppSelector(getProfileUtils);
  const regions = useAppSelector(pharmacistRegions);
  const coordinates = useAppSelector(userCoordinates);
  const userPharmacy = useAppSelector(userPharmacyInfo);
  const isPharmacyAdminLogin = useAppSelector(isPharmacyAdminLoginSelector);
  const userType = useAppSelector(getTypeOfUser);
  const userLanguage = useAppSelector(language);
  const isFrench = userLanguage === Language.fr;

  const { reviewDetails } = proInfoData ?? {};
  const { questionValues, arrivalTotalReviews } = reviewDetails ?? {};

  const handleImageChange = (values: FormikValues) => {
    const { image, isPictureUploaded } = values ?? {};
    let form = null;

    if (image) {
      form = new FormData();
      form.append('image', image);
    }
    formik.setFieldValue('form', form);
    formik.setFieldValue('image', image);
    formik.setFieldValue('isPictureUploaded', isPictureUploaded);

    dispatch(resetDialog());
  };

  const handleChange = (e: boolean) => {
    dispatch(istermsAgreed(e));
  };

  // Signups
  const pharmacistSignup = pharmacistSignupFields({
    proUtils,
    regions,
    showTermsAndConditions,
    handleChange,
    contactPreferences: proUtils?.contactPreferences ?? [],
    userType,
    showPictureUploadModal: showPictureUploadModal(handleImageChange),
    imageName: formik.values?.image?.name ?? '',
  });

  const professionalSignup = professionalSignupFields({
    proUtils,
    regions,
    showTermsAndConditions,
    handleChange,
    contactPreferences: proUtils?.contactPreferences ?? [],
    showPictureUploadModal: showPictureUploadModal(handleImageChange),
    imageName: formik.values?.image?.name ?? '',
    isIncorporated: formik.values?.incorporated === 'true',
    userType,
  });

  const pharmacySignup = pharmacySignupFields({
    softwares: proUtils?.softwares ?? [],
    provinces: proUtils?.provinces ?? [],
    banners: proUtils?.banners ?? [],
    contactPreferences: proUtils?.contactPreferences ?? [],
    showTermsAndConditions,
    handleChange,
  });

  // Profiles
  const pharmacistProfile = pharmacistFields({
    languages: allLanguages,
    proInfoData,
    proUtils,
    regions,
    isSelfService,
    handleDelete,
    setSelected,
    general,
    contactPreferences: proUtils?.contactPreferences ?? [],
    isFrench,
    userType,
    showGeo: showMyGeoAvailability(setSelected),
  });

  const professionalProfile = professionalFields({
    languages: allLanguages,
    general,
    proInfoData,
    proUtils,
    regions,
    isSelfService,
    handleDelete,
    setSelected,
    contactPreferences: proUtils?.contactPreferences ?? [],
    isIncorporated: formik.values?.incorporated === 'true',
    handleEditAccount: handleEdit,
    totalReviews: arrivalTotalReviews,
    questionValues,
    userType,
    isFrench,
    showGeo: showMyGeoAvailability(setSelected),
  });

  const pharmacyProfile = pharmacyField({
    languages: allLanguages,
    softwares: userPharmacy?.softwares || [],
    provinces: coordinates?.provinces ?? [],
    isSelfService,
    handleDelete,
    setSelected,
    contactPreferences: userPharmacy?.contactPreferences ?? [],
    isPharmacyAdmin: isPharmacyAdminLogin,
  });

  const formConfig = {
    [self]: {
      [professional]: {
        signup: professionalSignup,
        profile: professionalProfile,
      },
      [pharmacy]: {
        signup: pharmacySignup,
        profile: pharmacyProfile,
      },
    },
    [full]: {
      [pharmacist]: {
        signup: pharmacistSignup,
        profile: pharmacistProfile,
      },
      [technician]: {
        signup: pharmacistSignup,
        profile: pharmacistProfile,
      },
      [student]: {
        signup: pharmacistSignup,
        profile: pharmacistProfile,
      },
      [pharmacy]: {
        signup: pharmacySignup,
        profile: pharmacyProfile,
      },
    },
  };

  return { formConfig };
};

export default useProfileForm;
